// Sprint 12.5 — Workspace data merge. Joins the three REAL planes by appId:
//   • recent-apps (localStorage fast cache)  • Supabase apps (durable cloud list)
//   • /v1/analytics (real per-app views/installs/interactions/credits, D1)
// No fabricated data: analytics fields are undefined when the endpoint has no row,
// and status is derived only from what the record actually supports.
import { getRecentApps, saveRecentApp, type RecentApp } from './recent-apps';
import { listCreatorApps } from './supabase-bridge';
import { fetchServerApps } from './creator-apps-api';
import { t } from './i18n';

export interface AppAnalyticsRow {
  appId: string; name?: string; views?: number; installs?: number; interactions?: number;
  creditsUsed?: number; aiCostUsd?: number;
}
/** §S679 additive server fields the /v1/creator/apps rows carry (D1 truth). */
type ServerShareState = { hasDraft?: boolean; isPublic?: boolean; slug?: string | null; publishedAt?: number | null };
export interface WorkspaceApp extends RecentApp {
  /** real analytics if the endpoint returned a row for this appId, else undefined */
  analytics?: AppAnalyticsRow;
  /** honest derived status — 'live' only when a deployed (non-preview) URL exists */
  status: 'live' | 'preview';
  /** server truth (§S679 rows): an unpublished draft build exists */
  hasDraft?: boolean;
  /** server truth (§S679 rows): listed publicly (app_metadata.public === 1) */
  isPublic?: boolean;
}

/** Fetch the real per-app analytics rollup keyed by appId. Empty map on failure. */
export async function loadAnalyticsMap(): Promise<Map<string, AppAnalyticsRow>> {
  try {
    const res = await fetch('/v1/analytics', { credentials: 'include' });
    if (!res.ok) return new Map();
    const body = (await res.json()) as { apps?: AppAnalyticsRow[] };
    const map = new Map<string, AppAnalyticsRow>();
    for (const a of body.apps ?? []) if (a?.appId) map.set(a.appId, a);
    return map;
  } catch {
    return new Map();
  }
}

/** Like loadAnalyticsMap, but an endpoint failure returns null so callers can
 *  keep an honest '\u2014' instead of painting a fake zero. */
export async function loadAnalyticsMapStrict(): Promise<Map<string, AppAnalyticsRow> | null> {
  try {
    const res = await fetch('/v1/analytics', { credentials: 'include' });
    if (!res.ok) return null;
    const body = (await res.json()) as { apps?: AppAnalyticsRow[] };
    const map = new Map<string, AppAnalyticsRow>();
    for (const a of body.apps ?? []) if (a?.appId) map.set(a.appId, a);
    return map;
  } catch {
    return null;
  }
}

/** Restore any cloud apps missing from the local cache (eviction / new-device recovery). */
export async function hydrateFromCloud(): Promise<boolean> {
  let restored = false;
  // §7C-HR — DURABLE server source first: the gateway lists owned apps from D1
  // app_metadata via the authenticated creator session (same-origin cookie). This
  // is what makes a fresh browser / cleared cache see owned apps. The localStorage
  // cache below is just a hydration target, never the source of truth.
  const server = await fetchServerApps();
  if (server.length) {
    const have = new Set(getRecentApps().map((a) => a.appId));
    for (const a of server) {
      if (!have.has(a.appId)) { saveRecentApp(a); restored = true; }
    }
  }
  // Secondary: Supabase-direct list (covers any client-upserted apps not in D1).
  const cloud = await listCreatorApps();
  if (cloud.length) {
    const localIds = new Set(getRecentApps().map((a) => a.appId));
    for (const c of cloud.filter((x) => !localIds.has(x.appId))) {
      saveRecentApp({
        appId: c.appId,
        url: c.previewUrl ?? `/preview/${c.appId}/`,
        ogImageUrl: c.ogImageUrl ?? '',
        prompt: c.prompt,
        manifest: { name: c.name, primaryColor: c.primaryColor ?? '#F4477E' } as RecentApp['manifest'],
        createdAt: Date.now(),
        versions: []
      });
      restored = true;
    }
  }
  return restored;
}

const recency = (a: RecentApp): number => a.updatedAt ?? a.createdAt;

/** De-dup by appId then by logical name (newest wins) — same contract as MyApps. */
export function uniqueApps(apps: RecentApp[]): RecentApp[] {
  const byId = new Map<string, RecentApp>();
  for (const app of apps) {
    if (!app?.appId) continue;
    const e = byId.get(app.appId);
    if (!e || recency(app) > recency(e)) byId.set(app.appId, app);
  }
  const byKey = new Map<string, RecentApp>();
  for (const app of byId.values()) {
    const key = (app.manifest?.name || '').trim().toLowerCase() || app.appId;
    const e = byKey.get(key);
    if (!e || recency(app) > recency(e)) byKey.set(key, app);
  }
  return Array.from(byKey.values()).sort((a, b) => recency(b) - recency(a));
}

/** Merge the local list with an analytics map into the enriched Workspace model. */
export function mergeWorkspace(apps: RecentApp[], analytics: Map<string, AppAnalyticsRow>): WorkspaceApp[] {
  return uniqueApps(apps).map((a) => {
    const share = a as RecentApp & ServerShareState;
    return {
      ...a,
      analytics: analytics.get(a.appId),
      // Status truth: 'live' ONLY when a deployed URL exists. A client-synthesized
      // relative '/preview/…' fallback is not proof of deployment → 'preview'.
      status: a.url && !a.url.startsWith('/preview/') ? 'live' : 'preview',
      hasDraft: share.hasDraft === true ? true : undefined,
      isPublic: share.isPublic === true ? true : undefined
    };
  });
}

/** A real activity feed derived from the app records (no fabricated events). */
export interface ActivityItem { kind: 'created' | 'updated' | 'installs'; appId: string; name: string; ts: number; detail?: string; }
export function deriveActivity(apps: WorkspaceApp[]): ActivityItem[] {
  const items: ActivityItem[] = [];
  for (const a of apps) {
    const name = a.manifest?.name || a.appId;
    if (a.updatedAt && a.updatedAt > a.createdAt) {
      items.push({ kind: 'updated', appId: a.appId, name, ts: a.updatedAt, detail: a.versions?.length ? t('wsx.versionsN', { n: a.versions.length }) : undefined });
    }
    items.push({ kind: 'created', appId: a.appId, name, ts: a.createdAt });
    if (a.analytics?.installs) items.push({ kind: 'installs', appId: a.appId, name, ts: a.updatedAt ?? a.createdAt, detail: t('wsx.installsN', { n: a.analytics.installs }) });
  }
  return items.sort((x, y) => y.ts - x.ts).slice(0, 6);
}
